import { z } from 'zod'

import {
  CreateBrandedEnum,
  EnumBuilder,
  type ZodInfer,
} from '@mntn-dev/utility-types'

import { ModelFilterSchema, ModelRangeSchema } from '../model-filter-schema.ts'
import {
  CostMarginPercentSchema,
  DescriptionSchema,
  DollarsSchema,
  NameSchema,
  TranslationsSchema,
} from './property.models.ts'
import { TagFilterSchema, TagIdsByCategoryMapSchema } from './tag.models.ts'
import { PackageIdSchema, PackageUrnSchema } from './unique-id.models.ts'

/**
 * PackageVisibility
 */
export const [
  PackageVisibilityValues,
  PackageVisibilitySchema,
  PackageVisibilityEnum,
] = EnumBuilder('external', 'internal')
export type PackageVisibility = ZodInfer<typeof PackageVisibilitySchema>

/**
 * PackageKey
 */
export const [PackageKeys, PackageKeySchema, PackageKeyEnum] = EnumBuilder(
  'change_order_package',
  'legacy_quickframe_package'
)
export type PackageKey = ZodInfer<typeof PackageKeySchema>

/**
 * PackageSource
 */
export const [
  PackageSources,
  PackageSourceSchema,
  PackageSourceEnum,
  unbrandPackageSource,
] = CreateBrandedEnum(
  { brand: 'PackageSource' },
  'ctv',
  'linkedin',
  'meta',
  'quickframe',
  'tiktok'
)
export type PackageSource = ZodInfer<typeof PackageSourceSchema>

/**
 * PackageTranslations
 */
export const PackageTranslationsSchema = TranslationsSchema(
  'name',
  'description'
)
export type PackageTranslations = ZodInfer<typeof PackageTranslationsSchema>

/**
 * PackageStatus
 */
export const [PackageStatuses, PackageStatusSchema, PackageStatusEnum] =
  EnumBuilder('draft', 'published', 'archived')
export type PackageStatus = ZodInfer<typeof PackageStatusSchema>

/**
 * PackagePayoutSchema
 */
export const PackagePayoutMax = 1_000_000
export const PackagePayoutSchema = DollarsSchema(PackagePayoutMax)
export type PackagePayout = ZodInfer<typeof PackagePayoutSchema>

/**
 * PackageFormat
 */
export const [PackageFormats, PackageFormatSchema, PackageFormatEnum] =
  EnumBuilder('animation', 'live-action', 'post-production', 'other')
export type PackageFormat = ZodInfer<typeof PackageFormatSchema>

/**
 * PackageDomainSelectModel
 */
export const PackageDomainSelectModelSchema = z.object({
  packageId: PackageIdSchema,
  packageUrn: PackageUrnSchema,
  packageKey: PackageKeySchema.optional(),
  visibility: PackageVisibilitySchema,
  packageSource: PackageSourceSchema,
  packageFormat: PackageFormatSchema,
  name: NameSchema,
  description: DescriptionSchema,
  translations: PackageTranslationsSchema.optional(),
  cost: PackagePayoutSchema,
  costMarginPercent: CostMarginPercentSchema.optional(),
  costPlusMargin: DollarsSchema(),
  status: PackageStatusSchema,
})

export type PackageDomainSelectModel = ZodInfer<
  typeof PackageDomainSelectModelSchema
>

/**
 * PackageDomainInsertModel
 */
export const PackageDomainInsertModelSchema =
  PackageDomainSelectModelSchema.omit({
    packageUrn: true,
    packageKey: true,
    status: true, // Always inserted as draft
    visibility: true, // Always inserted as external
    costPlusMargin: true,
  }).partial({ packageId: true, costMarginPercent: true })

export type PackageDomainInsertModel = ZodInfer<
  typeof PackageDomainInsertModelSchema
>

/**
 * PackageDomainUpdateModel
 */
export const PackageDomainUpdateModelSchema =
  PackageDomainSelectModelSchema.pick({
    packageSource: true,
    packageFormat: true,
    visibility: true,
    name: true,
    description: true,
    translations: true,
    cost: true,
    status: true,
  })
    .extend({
      tags: TagIdsByCategoryMapSchema,
      costMarginPercent:
        PackageDomainSelectModelSchema.shape.costMarginPercent.nullable(),
    })
    .partial()

export type PackageDomainUpdateModel = ZodInfer<
  typeof PackageDomainUpdateModelSchema
>

/**
 * PackageDomainSelectModelFilters
 */
export const PackageDomainSelectModelFiltersSchema = z
  .object({
    packageId: ModelFilterSchema(PackageIdSchema),
    packageUrn: ModelFilterSchema(PackageUrnSchema),
    packageKey: ModelFilterSchema(PackageKeySchema),
    packageSource: ModelFilterSchema(PackageSourceSchema),
    packageFormat: ModelFilterSchema(PackageFormatSchema),
    status: ModelFilterSchema(PackageStatusSchema),
    tags: TagFilterSchema,
    visibility: ModelFilterSchema(PackageVisibilitySchema),
    cost: ModelRangeSchema(PackagePayoutSchema),
  })
  .partial()

export type PackageDomainSelectModelFilters = ZodInfer<
  typeof PackageDomainSelectModelFiltersSchema
>

export const packagesByStatus =
  (statuses: PackageStatus[]) =>
  ({ status }: PackageDomainSelectModel) =>
    statuses.includes(status)
